import React, { useEffect, useState, useContext } from 'react'
import { useParams, Redirect } from 'react-router-dom'
import { Layout } from '../../components/Layout'
import { LaboratoryService } from '../../services/Laboratory'
import { SchedulerService } from '../../services/Scheduler'
import { FilesService } from '../../services/Files'
import { Context } from '../../Context'
import { GLOBALS } from '../../utils/Globals'
import PDFViewer from 'pdf-viewer-reactjs'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'
import { GuacamoleService } from '../../services/Guacamole'
import { PodsService } from '../../services/Pods'
import MultiSessionManager from '../../components/RemoteConsoles/MultiSessionManager'
import './styles.scss'
import { VirtualMachineService } from '../../services/VirtualMachine'
import { getServerInfo } from '../../utils/helpers/networkInfo.helper'
import useEventAlert from '../../hooks/useEventAlert'

export class BlobStorageService {
  static getFileUrl(filePath) {
    // Obtener la URL base usando getServerInfo
    const blobUrl = getServerInfo('blob')

    // Eliminar barras iniciales para evitar barras dobles
    const cleanPath = filePath.replace(/^\/+/, '')

    // Devolver la URL completa
    return `${blobUrl}${cleanPath}`
  }

  // Método auxiliar para verificar si existe un archivo
  static async checkFileExists(filePath) {
    const url = this.getFileUrl(filePath)
    try {
      const response = await fetch(url, { method: 'HEAD' })
      return response.ok
    } catch (error) {
      console.error('Error al verificar archivo:', error)
      return false
    }
  }
}

export const Laboratory = () => {
  const { isAuth: token, userLogged } = useContext(Context)
  const { id: LaboratoryId } = useParams()
  const [error, setError] = useState(null)
  const [Laboratory, setLaboratory] = useState({})
  const [resources, setResources] = useState([])
  const [podForSchedul, setPodForSchedul] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [tokens, setTokens] = useState({})
  const [redirectTo, setRedirectTo] = useState(null)
  const [isNextOverLab, setIsNextOverLab] = useState(false)
  const [endDateTime, setEndDateTime] = useState(null)
  const [, setTimeDebugInfo] = useState(null)
  const [hasActiveReservation, setHasActiveReservation] = useState(false)

  const [pdfUrl, setPdfUrl] = useState('')
  const [imageUrl, setImageUrl] = useState('')

  const config = {
    title: Laboratory.name || '',
    icon: GLOBALS.menu.successviewLab.icon,
    backlink: GLOBALS.menu.reserve.linkList,
  }

  const TransformVirtualMachineRequest = (virtualMachineSelected, screenWidth, screenHeight) => {
    let connectionType
    switch (virtualMachineSelected.connection_type) {
      case 'R':
        connectionType = 'rdp'
        break
      case 'S':
        connectionType = 'ssh'
        break
      case 'V':
        connectionType = 'vnc'
        break
      case 'T':
        connectionType = 'telnet'
        break
      default:
        connectionType = 'ssh'
    }

    return {
      secretKey: 'GvfTBBRdtoPqk4FzxcXrIPxqnRSz0MZH',
      tokenObject: {
        connection: {
          type: connectionType,
          settings: {
            hostname: virtualMachineSelected.ip,
            port: virtualMachineSelected.port,
            username: virtualMachineSelected.username,
            password: virtualMachineSelected.password,
            'enable-drive': true,
            'create-drive-path': true,
            security: 'any',
            'ignore-cert': true,
            'enable-wallpaper': false,
          },
        },
        aditionalInformation: {
          userId: userLogged.id,
          labId: LaboratoryId,
          vmId: virtualMachineSelected.id,
          podId: podForSchedul.pod_id,
          schedulerId: podForSchedul.id,
        },
      },
    }
  }

  // Alerta 10 minutos antes - SOLO cuando hay reserva activa y acceso a máquinas
  const { debugInfo: tenMinDebugInfo } = useEventAlert(
    hasActiveReservation && endDateTime ? endDateTime : null,
    10,
    () => {
      alert('El laboratorio se cerrará en 10 minutos')
      setIsNextOverLab(false)
    }
  )

  // Alerta al finalizar - SOLO cuando hay reserva activa y acceso a máquinas
  const { debugInfo: endLabDebugInfo } = useEventAlert(
    hasActiveReservation && endDateTime ? endDateTime : null,
    0,
    async () => {
      alert('El laboratorio ha finalizado')

      // Revertir las máquinas virtuales si hay recursos
      if (resources && resources.virtualMachines) {
        for (const vm of resources.virtualMachines) {
          await handleRevertToFirstSnapshot(vm.display_name)
        }
      }

      setRedirectTo({
        pathname: config.backlink,
        state: { success: true },
      })
    }
  )

  // Guardar información de depuración cuando cambie - SOLO cuando hay reserva activa
  useEffect(() => {
    if (hasActiveReservation && (tenMinDebugInfo || endLabDebugInfo)) {
      setTimeDebugInfo({
        ...tenMinDebugInfo,
        ...endLabDebugInfo,
        endDateTime: endDateTime ? new Date(endDateTime).toISOString() : 'No establecido',
      })
    } else if (!hasActiveReservation) {
      // Limpiar información de depuración cuando no hay reserva activa
      setTimeDebugInfo(null)
    }
  }, [tenMinDebugInfo, endLabDebugInfo, endDateTime, hasActiveReservation])

  const renderPDF = () => {
    if (Laboratory.file) {
      if (pdfUrl) {
        return (
          <PDFViewer
            document={{ url: pdfUrl }}
            scale={1.4}
            navbarOnTop
            hideRotation={false}
            hidePageButtons={false}
          />
        )
      } else {
        return <p>No hay documento disponible</p>
      }
    }
  }

  const renderImage = () => {
    if (Laboratory.topology) {
      if (imageUrl) {
        return <img src={imageUrl} alt="Topología" />
      } else {
        return <p>No hay imagen disponible</p>
      }
    }
  }

  // Convertir endDate a formato ISO si es necesario
  const formatEndDate = (endDate) => {
    // Si no hay fecha, devolvemos null
    if (!endDate) return null

    try {
      // Si ya es una fecha, la convertimos a ISO
      if (endDate instanceof Date) {
        return endDate.toISOString()
      }

      // Intentamos crear un Date y convertirlo a ISO
      const date = new Date(endDate)
      if (isNaN(date.getTime())) {
        console.error('Error: La fecha de finalización no es válida:', endDate)
        return null
      }

      return date.toISOString()
    } catch (e) {
      console.error('Error al formatear la fecha de finalización:', e)
      return null
    }
  }

  // Usar un useEffect separado para actualizar endDateTime para evitar bloqueos
  useEffect(() => {
    const checkReservation = async () => {
      try {
        if (userLogged.role === 'U' || userLogged.role === 'T') {
          // Verificar si hay una reserva activa para este usuario y laboratorio
          const podsForUserandLab = await SchedulerService.getpodsForUserandLab(
            userLogged.id,
            LaboratoryId,
            token
          )

          // Si hay reservas, procesar la fecha de finalización
          if (podsForUserandLab && podsForUserandLab.length > 0) {
            // Obtener la hora actual para filtrar la reserva correcta
            const currentTime = new Date()

            // Filtrar para encontrar la reserva activa o próxima a iniciar
            // Ordenamos las reservas por tiempo de inicio y buscamos la primera que aún no ha terminado
            const sortedReservations = [...podsForUserandLab].sort((a, b) => {
              return new Date(a.startDate) - new Date(a.startDate)
            })

            // Buscar la reserva activa (donde la hora actual está entre la hora de inicio y fin)
            const activeReservation = sortedReservations.find((reservation) => {
              const startTime = new Date(reservation.startDate)
              const endTime = new Date(reservation.endDate)
              return currentTime >= startTime && currentTime <= endTime
            })

            // Si no hay reserva activa, buscar la próxima reserva
            const nextReservation = !activeReservation
              ? sortedReservations.find((reservation) => {
                  return new Date(reservation.startDate) > currentTime
                })
              : null

            // SOLO usar la reserva activa para las alertas de tiempo
            // Las reservas futuras no deben activar alertas
            if (activeReservation && activeReservation.endDate) {
              // Formatear y establecer la fecha de finalización
              const formattedEndDate = formatEndDate(activeReservation.endDate)
              if (formattedEndDate) {
                setEndDateTime(formattedEndDate)
                // También actualizar podForSchedul para mostrar la información correcta
                setPodForSchedul(activeReservation)
                // IMPORTANTE: Establecer hasActiveReservation en true
                setHasActiveReservation(true)
              }
            } else if (nextReservation) {
              // Para reservas futuras, solo actualizar podForSchedul pero NO endDateTime
              setPodForSchedul(nextReservation)
              setEndDateTime(null) // No activar alertas para reservas futuras
              setHasActiveReservation(false) // No hay reserva activa
              console.log('⏰ Reserva futura encontrada:', nextReservation)
              console.log('❌ hasActiveReservation establecido en false')
            } else if (sortedReservations[0]) {
              // Para cualquier otra reserva, solo actualizar podForSchedul
              setPodForSchedul(sortedReservations[0])
              setEndDateTime(null) // No activar alertas
              setHasActiveReservation(false) // No hay reserva activa
            }
          } else {
            setHasActiveReservation(false)
            setEndDateTime(null)
          }
        } else {
          // Para administradores, no hay reservas activas
          setHasActiveReservation(false)
          setEndDateTime(null)
        }
      } catch (error) {
        console.log('Error al verificar la reserva:', error)
        setHasActiveReservation(false)
        setEndDateTime(null)
      }
    }

    checkReservation()
  }, [userLogged.id, userLogged.role, LaboratoryId, token])

  useEffect(() => {
    let isMounted = true

    const fetchData = async () => {
      let podsForUserandLab = []
      let activeReservation = null
      try {
        setIsLoading(true)

        // Cargar datos básicos del laboratorio para TODOS los usuarios
        const laboratorySelected = await LaboratoryService.getById(LaboratoryId, token)

        // Cargar recursos solo si hay reservas activas (para usuarios y profesores)
        if ((userLogged.role === 'U' || userLogged.role === 'T') && hasActiveReservation) {
          // Obtener las reservas del usuario para este laboratorio
          podsForUserandLab = await SchedulerService.getpodsForUserandLab(
            userLogged.id,
            LaboratoryId,
            token
          )

          // Usar la reserva activa que ya se identificó anteriormente
          // Buscar la reserva activa en la lista de reservas
          const currentTime = new Date()
          activeReservation = podsForUserandLab.find((reservation) => {
            const startTime = new Date(reservation.startDate)
            const endTime = new Date(reservation.endDate)
            return currentTime >= startTime && currentTime <= endTime
          })

          // Si hay una reserva activa, proceder a obtener los recursos del pod específico
          if (activeReservation && activeReservation.pod) {
            const resources = await PodsService.getById(activeReservation.pod.id, token)
            const tokens = {}

            setResources(resources.virtualMachines)

            for (const vm of resources.virtualMachines) {
              const WsUrlGenerated = await GuacamoleService.getEncryptToken(
                TransformVirtualMachineRequest(vm),
                token
              )
              tokens[vm.id] = {
                token: WsUrlGenerated.encryptedToken,
                displayName: vm.display_name,
                resourceType: vm.type,
                host: vm.host_address_pdu,
                load: vm.port_pdu,
                port: vm.port,
                host_vm: vm.ip
              }
            }
            setTokens(tokens)
          }
        }

        if (isMounted) {
          setLaboratory(laboratorySelected)
          // Usar la reserva activa específica en lugar del primer elemento
          if (activeReservation) {
            setPodForSchedul(activeReservation)
          } else if (podsForUserandLab.length > 0) {
            setPodForSchedul(podsForUserandLab[0])
          }

          if (laboratorySelected.file) {
            // Using FilesService with the new 'file' type for PDFs
            const pdfUrl = FilesService.getByName(laboratorySelected.file)
            setPdfUrl(pdfUrl)
          }

          if (laboratorySelected.topology) {
            // Using FilesService with the new 'topology' type for images
            const imageUrl = FilesService.getByName(laboratorySelected.topology)
            setImageUrl(imageUrl)
          }
        }
      } catch (e) {
        if (isMounted) {
          setError(e.message)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      isMounted = false
    }
  }, [LaboratoryId, token, userLogged.id, userLogged.role, hasActiveReservation])

  // Obtener el primer snapshot y revertir la VM
  const handleRevertToFirstSnapshot = async (vmName) => {
    setIsLoading(true)
    try {
      const firstSnapshot = await VirtualMachineService.getFirstSnapshot(vmName, token)

      if (firstSnapshot) {
        // Revertir la VM al primer snapshot
        await VirtualMachineService.revertSnapshot(vmName, firstSnapshot, token)
      } else {
        console.warn('No se encontraron snapshots para esta máquina virtual.')
      }
    } catch (error) {
      console.error('Error al revertir al primer snapshot:', error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const timeAlert = () => {
    if (isNextOverLab) {
      return <p className="alert-message">{'El laboratorio se cerrara en 5 minutos'}</p>
    }
    return <></>
  }

  if (redirectTo) {
    return <Redirect to={redirectTo} />
  }

  const availablePods = () => {
    let pod = <></>
    if (userLogged.role === 'S') {
      // Administrador: ve todos los pods del laboratorio
      pod = (
        <ul>
          {Laboratory.pods.map((pod) => (
            <li key={pod.id}>
              {pod.name}
              <a
                href={`${GLOBALS.menu.pods.viewpod}/${pod.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  type="button"
                  icon="pi pi-chevron-circle-right"
                  className="p-button-primary"
                  tooltip="Ver"
                ></Button>
              </a>
            </li>
          ))}
        </ul>
      )
      return pod
    }
    /*     return <li>No hay pods disponibles para este laboratorio</li>; */
  }

  return (
    <Layout>
      <>
        {isLoading ? (
          <div className="loading-container">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '4em' }}></i>
          </div>
        ) : error ? (
          <Message severity="error" text={error}></Message>
        ) : (
          <div className="laboratory-container">
            <div className="header">
              <h1>
                {config.icon && <i className={config.icon}></i>}
                {config.title}
              </h1>
              {timeAlert()}
            </div>

            <div className="content">
              <div className="first-session">
                <div className="description">
                  <span>
                    <p>Introducción: {Laboratory.text}</p>
                  </span>
                  <span>
                    <p>Notas: {Laboratory.description}</p>
                  </span>
                </div>

                <div className="pods">
                  {Laboratory.pods && Laboratory.pods.length > 0 ? (
                    <>
                      {availablePods()}
                      <ul>
                        <li>
                          Topologia
                          <a href={`${imageUrl}`} target="_blank" rel="noopener noreferrer">
                            <Button
                              type="button"
                              icon="pi pi-chevron-circle-right"
                              className="pod-button"
                              tooltip="Topologia"
                            ></Button>
                          </a>
                        </li>
                      </ul>
                    </>
                  ) : (
                    <p>No hay pods disponibles</p>
                  )}
                </div>
              </div>

              <div className="multiSession-manager-guacamole-console">
                {/* Para estudiantes: Solo MultiSessionManager cuando tienen reserva activa */}
                {userLogged.role === 'U' &&
                  hasActiveReservation &&
                  tokens &&
                  Object.keys(tokens).length > 0 && (
                    <MultiSessionManager
                      ResoucesTokens={tokens}
                      topologyTab={renderImage()}
                      guideTab={renderPDF()}
                    />
                  )}

                {/* Para profesores: MultiSessionManager cuando hay reserva activa, documentos cuando no */}
                {userLogged.role === 'T' && (
                  <>
                    {/* Si tiene reserva activa, mostrar SOLO MultiSessionManager */}
                    {hasActiveReservation && tokens && Object.keys(tokens).length > 0 ? (
                      <MultiSessionManager
                        ResoucesTokens={tokens}
                        topologyTab={renderImage()}
                        guideTab={renderPDF()}
                      />
                    ) : (
                      /* Si NO hay reserva activa, mostrar SOLO documentos (modo visualización) */
                      <div className="professor-documents">
                        <h3>Documentos del Laboratorio</h3>
                        <div className="documents-container">{renderPDF()}</div>
                      </div>
                    )}
                  </>
                )}

                {/* Para administradores: Solo documentos */}
                {userLogged.role === 'S' && renderPDF()}
              </div>
            </div>
          </div>
        )}
      </>
    </Layout>
  )
}
